
import { ActionTree } from 'vuex';
import { GspModuleState } from './types';
import { RootState } from '../../types';
import { defaultBackendGspModule } from '@/shared/backend/gspModule';
import { Logger } from 'fsts';
import module_utils from '../module_utils';
import gspModule, { Value, GspModule } from '@/shared/model/gspModule';
import { AxiosResponse } from 'axios';
import i18n from '@/i18n';
import { SearchParams } from '@/shared/model/searchParams';
// import { CONST } from '../utils/Constants';

// Module-level variable to hold the running promise
let activeFetchPromise: Promise<void> | null = null;

const logger = new Logger('actions.gspModules');
export const actions: ActionTree<GspModuleState, RootState> = {
  /**
   * Master Cache Loader (Called from app.ts / App.vue or multiple widgets)
   * Safe to call from 10 components at the same time: only 1 network call will be made.
   */
  async fetchAllGspModules({ commit, dispatch, getters, state }, force = false): Promise<void> {
    const isCacheValid = getters['isAllGspModulesCacheValid'];

    // If cache is already valid and has data, skip network entirely
    if (!force && isCacheValid && state.allGspModules.length > 0) {
      logger.info('✓ Using cached allGspModules');
      return;
    }

    // If a request is already running, wait for it instead of firing a duplicate
    if (activeFetchPromise) {
      logger.info('⏳ Joining active in-flight fetchAllGspModules request...');
      return activeFetchPromise;
    }

    // 3. Initiate the single master fetch
    activeFetchPromise = (async () => {
      try {
        const fullParams: SearchParams = {
          dataOption: {
            page: 1,
            itemsPerPage: 500,
            sortBy: [],
            sortDesc: [false],
            groupBy: [],
            groupDesc: [],
            multiSort: false,
            mustSort: false,
          },
          filter: '',
          orClauseFieldsIds: ['name'],
          andClauseFieldsIds: [],
        };

        const response = await defaultBackendGspModule.getGspModules(fullParams);
        const parsed = (response.data.value || []).map((x: any) => gspModule.parse(x));

        // Store master full dataset
        commit('setAllGspModules', parsed);
        logger.info(`✓ Master cache loaded: ${parsed.length} modules`);

        // Refresh grouped cache automatically now that we have 100% of the dataset
        await dispatch('getModulesGrouped', true);
      } catch (e: any) {
        module_utils.error('fetchAllGspModules failed', commit, e, logger);
        throw e;
      } finally {
        // ALWAYS reset promise so future force calls can execute
        activeFetchPromise = null;
      }
    })();

    return activeFetchPromise;
  },

  /**
   * Standard Table Action (Used by GspModulesComponent / D4yTable)
   * Unaffected by master cache; performs standard server-side paging (25 items).
   */
  async getGspModules(
    { commit, getters },
    payload?: { searchParams?: SearchParams; serviceId?: number }
  ): Promise<Value> {
    const searchParams = payload?.searchParams ?? getters.getGspModulesSearchParams;

    commit('setGspModulesIsLoading', true);
    try {
      const response = await defaultBackendGspModule.getGspModules(searchParams, payload?.serviceId);

      commit('setGspModules', response.data);
      return response.data;
    } catch (e: any) {
      module_utils.error('getGspModules error', commit, e, logger);
      throw e;
    } finally {
      commit('setGspModulesIsLoading', false);
    }
  },


  async getGspModulesOld(
    { commit, dispatch, getters, state },
    payload?: { searchParams?: SearchParams; serviceId?: number; force?: boolean }
  ) {
    let searchParams = payload?.searchParams ?? getters.getGspModulesSearchParams;
    const hasFilter = !!searchParams?.filter && searchParams.filter.trim().length > 0;
    const force = payload?.force ?? false;

    // Use cache if valid // Do NOT use cache if there is an active filter or if forced
    if (!force && !hasFilter && getters['isGspModulesCacheValid']) {
      logger.info(`✓ Using cached gspModules (age: ${getters['getGspModulesCacheInfo'].ageInSeconds}s)`);
      const cachedGspModules = getters['getGspModules'];
      const cachedResponse: Value = {
        value: cachedGspModules,
        '@odata.count': Array.isArray(cachedGspModules) ? cachedGspModules.length : undefined
      };
      return Promise.resolve(cachedResponse);
      // return Promise.resolve(getters['getGspModules']);
    }

    commit('setGspModulesIsLoading', true);
    // let organisationId = rootGetters[CONST.getOrganisation].id;
    try {
      const response = await defaultBackendGspModule.getGspModules(searchParams, payload?.serviceId);

      commit('setGspModules', response.data);
      // Only set valid cache if this was an unfiltered fetch
      if (!hasFilter && !payload?.serviceId) {
        commit('setGspModulesCache'); // set cache
      }
      commit('setGspModulesIsLoading', false);

      // Refresh grouped cache automatically
      await dispatch('getModulesGrouped', true);

      logger.info('✓ gspModules fetched and cached');
      return response.data;
    } catch (e: any) {
      commit('setGspModulesIsLoading', false);
      module_utils.error('error', commit, e, logger);
      throw e;
    }
  },

  /**
   * Invalidate both caches after any `GspModule` change
   */
  invalidateAllCaches({ commit }) {
    logger.info('Invalidating all gspModule caches');
    commit('invalidateAllGspModulesCache');
    commit('invalidateModulesGroupedCache');
  },


  /**
   * Get grouped modules with caching
   * Returns cached data if cache is still valid
   */
  getModulesGrouped({ commit, getters, dispatch }, force: boolean = false): Promise<any[]> {
    // Check if cache is valid and not forced
    if (
      !force &&
      getters['isModulesGroupedCacheValid'] &&
      getters['getModulesGroupedCacheInfo'].isValid
    ) {
      logger.info(`✓ Using cached grouped modules (age: ${getters['getModulesGroupedCacheInfo'].ageInSeconds}s)`);
      return Promise.resolve(getters['getModulesGroupedByService']);
    }

    // Get grouped modules from getter (computed on-the-fly)
    const grouped = getters['getModulesGroupedByService'];
    console.log('zzzz grouped :>> ', grouped);

    // Update cache
    commit('setModulesGroupedCache', grouped);

    logger.info(`✓ Computed and cached grouped modules`);

    return Promise.resolve(grouped);
  },

  /**
   * Refresh grouped modules cache
   * Force recompute even if cache is valid
   */
  refreshModulesGrouped({ commit, getters }) {
    logger.info('Refreshing grouped modules cache...');
    const grouped = getters['getModulesGroupedByService'];
    commit('setModulesGroupedCache', grouped);
    return Promise.resolve(grouped);
  },

  /**
   * Invalidate grouped cache
   * Call after create/update/delete module
   */
  invalidateModulesGroupedCache({ commit }) {
    logger.info('Invalidating grouped modules cache');
    commit('invalidateModulesGroupedCache');
  },


  getGspModule({ commit, dispatch }, id: string) {
    return defaultBackendGspModule
      .getGspModule(id)
      .then((response: AxiosResponse<GspModule>) => {
        return response.data;
      })
      .catch((e: any) => {
        module_utils.error('error', commit, e, logger);
        throw e;
      });
  },
  updateGspModule({ commit, dispatch }, file: GspModule) {
    return defaultBackendGspModule
      .updateGspModule(file)
      .then(
        (
          response: AxiosResponse<{
            errors: [];
            id: string;
            isValid: boolean;
            result: GspModule;
          }>
        ) => {
          module_utils.ok(i18n.tc(`success.gspModule_update`), commit);
          dispatch('invalidateAllCaches');
          return response.data;
        }
      )
      .catch((e: any) => {
        module_utils.error('error', commit, e, logger);
        throw e;
      });
  },
  async deleteGspModule({ dispatch }, id: string): Promise<void> {
    await defaultBackendGspModule.deleteGspModule(id);
    // Reload master cache so badges and groups update across the entire app
    await dispatch('fetchAllGspModules', true);
  },

  deleteGspModuleOld({ commit, dispatch }, id: string) {
    return defaultBackendGspModule
      .deleteGspModule(id)
      .then(
        (
          response: AxiosResponse<{
            errors: [];
            id: string;
            isValid: boolean;
            result: GspModule;
          }>
        ) => {
          dispatch('invalidateAllCaches');
          return response;
        }
      )
      .catch((e: any) => {
        module_utils.error('error', commit, e, logger);
        throw e;
      });
  },





};
